import React, { useMemo } from 'react';
import styled from 'styled-components';

import Typography from 'antd/es/typography';
import Tag from 'antd/es/tag';

import { Row, Col } from 'components/layout';
import { Text } from 'components/Text';

import useSearcher from 'hooks/useSearcher';
import { useLocationSelect } from 'hooks/useSelect';
import useQueryParam from 'hooks/useQueryParam';
import useIndividualsLive from 'hooks/useIndividualsLive';
import useStats from 'hooks/useStats';
import { StatType } from 'services/stats';

import MainLayout from 'layouts/Main.layout';
import PageHeader from 'components/MainPageHeader';
import Grid from 'components/Grid';
import Table from 'components/Table';
import StatsCard from 'components/StatsCard';
import FilterBar from 'components/FilterBar';
import Search from 'components/Search';

import doorState from 'config/doorState';
import { formatDuration, formatDate } from 'helpers/date';
import { dateTimeFormat, timeFormat } from 'config/common';
import StatusTag from '../../Dashboard/components/StatusTag';

const IndividualsLivePageHeader = styled(PageHeader)`
  & .ant-page-header-content {
    padding-top: unset;
  }
`;

const ActiveIndividuals = () => {
  const locationSelect = useLocationSelect();
  const [searchVal, setSearchVal] = useQueryParam('s', '');

  const {
    isLoading,
    liveStats,
    summary,
  } = useIndividualsLive(locationSelect.value);

  const locationsLive = useStats(
    StatType.locationsLive,
    {
      location_id: locationSelect.value,
    },
    {
      initialStats: {
        doors: [],
        summary: {},
      },
      autoFetch: true,
      refreshInterval: 180000,
    }
  );

  const searcher = useSearcher([
    'full_name',
    'name',
  ]);
  
  const refreshedAt = useMemo(() => {
    const { doors = [] } = locationsLive.state.stats;
    const latestUpdate = doors.find(d => d.last_update)?.last_update;
    return latestUpdate
      ? formatDate(new Date(latestUpdate), timeFormat, { tzOffset: 0 })
      : null;
  }, [locationsLive.state.stats]);
  
  const employeesResults = useMemo(() => {
    const { doors: individualDetections = [] } = liveStats.state.stats;
    const { doors: doorsData = [] } = locationsLive.state.stats;
    
    const doorDataMap = doorsData.reduce((acc, door) => {
      acc[door.door_id] = door;
      return acc;
    }, {});
    
    const latestDetections = individualDetections.reduce((acc, detection) => {
      const employeeId = detection.employee_id || detection.full_name;
      const lastSeenTime = detection.last_time_seen;
      const timezone_offset = locationSelect?.value?.timezone_offset || 0;
      
      if (!acc[employeeId] || (lastSeenTime && lastSeenTime > acc[employeeId].last_time_seen)) {
        const doorData = doorDataMap[detection.door_id] || {};
        acc[employeeId] = {
          ...detection,
          ...doorData,
          timezone_offset,

          employee_id: detection.employee_id,
          full_name: detection.full_name,
          last_time_seen: detection.last_time_seen,
          tot_mins_seen: detection.tot_mins_seen,
        };
      }
      
      return acc;
    }, {});
    
    const uniqueDetections = Object.values(latestDetections);
    
    const sortedDetections = uniqueDetections.sort((a, b) => {
      const nameA = a.full_name || '';
      const nameB = b.full_name || '';
      return nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    });
    
    searcher.setData(sortedDetections);
    return searcher.search(searchVal);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searcher, liveStats.state.stats, locationsLive.state.stats, searchVal]);

  return (
    <MainLayout>
      <IndividualsLivePageHeader
        title="Active Individuals"
        icon="users"
        subTitle={null}
      >
        <Typography.Text style={{ fontSize: '12px', lineHeight: '20px', color: '#A4B0BA', marginLeft: '45px' }}>
          {refreshedAt ? `Refreshed at: ${refreshedAt}` : ''}
        </Typography.Text>
      </IndividualsLivePageHeader>
      <Grid gutter={24} columns={3} style={{ padding: '8px 24px 16px' }}>
        <StatsCard
          title="Active individuals in current shift"
          value={summary.totalIndividuals}
          titleAlign={['center', 'bottom']}
        />
        <StatsCard
          title="Loading individuals in current shift"
          value={summary.loadingIndividuals}
          titleAlign={['center', 'bottom']}
        />
        <StatsCard
          title="Unloading individuals in current shift"
          value={summary.unloadingIndividuals}
          titleAlign={['center', 'bottom']}
        />
      </Grid>

      <Row style={{ padding: '12px 24px', width: '100%' }}>
        <FilterBar
          title="Individuals"
          style={{ width: '100%' }}
        >
          <FilterBar.Slot align="right">
            <Search placeholder="Search individual" value={searchVal} onChange={setSearchVal} />
          </FilterBar.Slot>
        </FilterBar>
      </Row>

      <Row style={{ padding: '12px 24px' }}>
        <Col span={24}>
          <Table pagination={{ showSizeChanger: false }}
            dataSource={employeesResults}
            loading={isLoading || locationsLive.state.isLoading}
            rowKey={row => [row.container_code, row.full_name].join(':')}
          >
            <Table.Column type="link" title="Individual" dataIndex="full_name" to={({ item }) => `/individuals/${item.employee_id}`} />
            <Table.Column type="link" title="Door" dataIndex="name" to={({ item }) => `/doors/${item.door_id}`} />
            <Table.Column type="link" title="Equipment Code" dataIndex="container_code" to={({ item }) => {
              if (item.direction === 'LOAD') return `/trailers/${item.container_id}`;
              else return `/containers/${item.container_id}`;
            }} />
            <Table.Column type="datetime" title="Last Seen" dataIndex="last_time_seen" format={dateTimeFormat} align="center" tzOffset="timezone_offset" />
            <Table.Column title="Time Seen" dataIndex="tot_mins_seen" align="right" render={totMins => {
              if (totMins === undefined || totMins === null) return '-';
              return formatDuration(totMins * 60 * 1000, false);
            }} />
            <Table.Column 
              type="text" 
              title="Process" 
              dataIndex="direction" 
              render={(direction, { door_state }) => {
                if (!door_state || door_state === doorState.EMPTY) return '-';
                if (direction === 'LOAD') return 'Outbound';
                if (direction === 'UNLOAD') return 'Inbound';
                return '-';
              }}
            />
            <Table.Column 
              title="Status" 
              dataIndex="door_state" 
              align="center" 
              render={(doorState) => {
                if (!doorState) return "-";
                return <StatusTag status={doorState} />;
              }}
            />
            <Table.Column 
              title="Progress" 
              dataIndex="unloaded_percent" 
              align="center" 
              render={(unloaded_percent, { door_state }) => {
                if (!door_state || door_state === doorState.EMPTY) return "-";
                if (unloaded_percent === undefined || unloaded_percent === null) return "-";
                let value = (unloaded_percent * 100).toFixed(0);
                if (value !== "0" && value !== "100") {
                  value = `${value}`;
                }
                return <Tag color="processing">{value}%</Tag>;
              }}
            />
            <Table.Column 
              title="Speed" 
              dataIndex="relative_speed" 
              align="right" 
              width="110px" 
              render={(speed, { door_state, direction, default_direction }) => {
                const doorDir = direction || default_direction;
                if (!door_state || door_state === doorState.EMPTY) return "-";
                if (doorDir === "LOAD") return "N/A";
                if (!speed) return "-";

                return (
                  <Text
                    style={{
                      textTransform: "capitalize",
                      color: {
                        VERY_SLOW: "#F5222D",
                        SLOW: "#FAAD14",
                        NORMAL: "#52C41A",
                        FAST: "#42B0D5",
                      }[speed],
                    }}
                  >
                    {speed.toLowerCase()}
                  </Text>
                );
              }}
            />
            

            {/* <Table.Column type="datetime" title="First Seen" dataIndex="first_time_seen" format={dateTimeFormat} align="center" tzOffset="timezone_offset" /> */}

            {/* <Table.Column type="percentage" title="Progress" dataIndex="unloaded_percent" precision={0} /> */}
            {/* <Table.Column type="duration" title="Actual Time" dataIndex="current_minutes" inMins={true} withSecs={false} tooltip="Time from start of the loading/unloading" /> */}
            {/* <Table.Column type="duration" title="Target Time" dataIndex="tot_minutes_predicted" inMins={true} withSecs={false} noValue="N/A" tooltip="Time to finish the loading/unloading" /> */}
          </Table>
        </Col>
      </Row>
    </MainLayout>
  );
};

export default ActiveIndividuals;